import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Collapse, Table, InputGroup } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { ChevronDown, ChevronUp, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import sv from 'date-fns/locale/sv';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import {
  ASSET_CATEGORIES,
  ASSET_FORM_STYLES,
  BUILDING_TYPES,
  K1_BUILDING_TYPES,
  K1_LAND_IMPROVEMENT_TYPES,
  CategoryChip,
  buildDraftSchedule,
  dateToPeriod,
  formatCurrency,
  formatDate,
  formatMonthYear,
  formatNumber,
  getCategoryIcon,
  round2,
  unwrapData,
} from './assetUtils';

const YEAR_OPTIONS = [3, 4, 5, 7, 10].map((y) => ({ value: y, label: `${y} år` }));
// Markanläggningar: bokförd schablon 20 år (övriga) / 10 år (täckdiken &
// skogsvägar) — BFNAR 2017:3 p. 9.30, speglar IL 20 kap. 5 § (5 %/10 %).
const LAND_IMPROVEMENT_YEAR_OPTIONS = [
  { value: 20, label: '20 år (övriga markanläggningar)' },
  { value: 10, label: '10 år (täckdiken & skogsvägar)' },
];

const emptyForm = () => ({
  categoryCode: null,
  name: '',
  amount: '',
  years: 5,
  quantity: 1,
  grouping: 'per_unit',
  inServiceDate: new Date(),
  hasOpening: false,
  openingAcc: '',
  openingPeriod: '',
  // "Fler uppgifter" (optional; backend supports all of these):
  residual: '',
  acquisitionDate: null, // null = samma som ibruktagande
  serialNumber: '',
  location: '',
  vehicleType: 'personbil', // vehicles category only: personbil | lastbil
  k1PropertyManualPlan: false, // K1 property: accepted plan deviates from the normal table (spec §5.1)
  k1ParentAssetId: '', // K1 buildings: improvement of an existing building
  buildingType: null, // buildings category only: SKV A 2005:5 byggnadstyp
  k1BuildingType: null, // k1_buildings: SKV A 2005:5 key (backend-validated)
  k1LandImprovementType: null, // k1_land_improvements: IL 20:5 key
});

/**
 * CreateAssetForm — the guided two-step setup (Configure → Review) from the
 * asset plan §7. Used standalone from AssetList and prefilled from accepted
 * suggestions. Nothing is posted until "Klart — sköt det automatiskt" on the
 * review step; the schedule shown there is a client-side draft preview.
 *
 * Props:
 *   show, onHide  — modal control
 *   companyId
 *   prefill       — optional { name, amount, quantity, category_code,
 *                    in_service_date, suggestion_id, supplier_invoice_id }
 *   onCreated     — called with the created asset after a successful POST
 */
const CreateAssetForm = ({ show, onHide, companyId, prefill = null, onCreated }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [categories, setCategories] = useState(null); // tenant rows incl. accounts, when the endpoint exists
  const [showSchedule, setShowSchedule] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset + apply prefill every time the modal opens.
  useEffect(() => {
    if (!show) return;
    const next = emptyForm();
    if (prefill) {
      if (prefill.name) next.name = prefill.name;
      if (prefill.amount !== undefined && prefill.amount !== null) next.amount = String(prefill.amount);
      if (prefill.quantity) next.quantity = parseInt(prefill.quantity, 10) || 1;
      if (prefill.category_code) next.categoryCode = prefill.category_code;
      if (prefill.in_service_date) {
        const date = new Date(prefill.in_service_date);
        if (!isNaN(date.getTime())) next.inServiceDate = date;
      }
    }
    setForm(next);
    setStep(1);
    setShowSchedule(false);
    setShowDetails(false);
  }, [show, prefill]);

  // Tenant category rows carry the resolved account numbers (BAS-vintage
  // aware, seeded per tenant). Best-effort: the review step falls back to a
  // generic sentence when the endpoint isn't available.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(getApiUrl(`${companyId}/assets/categories`), { withCredentials: true });
        const data = unwrapData(response);
        const rows = Array.isArray(data) ? data : data?.categories;
        if (!cancelled && Array.isArray(rows)) setCategories(rows);
      } catch (error) {
        // Optional endpoint — accounts simply aren't shown in the drawer.
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // Active K1 buildings for the improvement link (spec §5.1, re-review RR4).
  const [activeBuildings, setActiveBuildings] = useState([]);
  const isK1BuildingsSelected = form.categoryCode === 'k1_buildings';
  useEffect(() => {
    if (!companyId || !isK1BuildingsSelected) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(getApiUrl(`${companyId}/assets`), {
          params: { status: 'active' }, withCredentials: true,
        });
        const data = unwrapData(response);
        const rows = Array.isArray(data) ? data : data?.assets;
        if (!cancelled && Array.isArray(rows)) {
          setActiveBuildings(rows.filter((a) => a.category_code === 'k1_buildings'));
        }
      } catch (error) {
        // Best-effort — without the list the improvement link simply isn't offered.
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, isK1BuildingsSelected]);

  const amountNumber = parseFloat(form.amount) || 0;
  const quantity = Math.max(parseInt(form.quantity, 10) || 1, 1);
  const isBuildings = form.categoryCode === 'buildings';
  const isLand = form.categoryCode === 'land' || form.categoryCode === 'k1_land';
  const isLandImprovements = form.categoryCode === 'land_improvements';
  const buildingType = BUILDING_TYPES.find((b) => b.value === form.buildingType) || null;
  // K1 (BAS 2018 K1): the collective pool has no per-asset plan at all, and
  // property depreciation is tax-linked — spec docs/bas-2026/
  // K1_ASSET_ACCOUNTING_SPEC.md. "Används i ungefär" stays as the p. 6.1
  // förteckning fact for pool assets.
  const isK1Buildings = form.categoryCode === 'k1_buildings';
  const isK1LandImprovements = form.categoryCode === 'k1_land_improvements';
  const k1BuildingType = K1_BUILDING_TYPES.find((b) => b.value === form.k1BuildingType) || null;
  const k1LandImprovementType = K1_LAND_IMPROVEMENT_TYPES.find((b) => b.value === form.k1LandImprovementType) || null;
  // Byggnader: nyttjandeperiod från byggnadstypens procentsats (SKV A 2005:5;
  // BFNAR 2017:3 p. 9.31 / K2 10.31 låter planen följa den). Mark skrivs inte
  // av alls (p. 9.29 / ÅRL 4:4).
  const usefulMonths = isLand
    ? 0
    : isBuildings
      ? (buildingType ? Math.round(1200 / buildingType.percent) : 0)
      : isK1Buildings
        ? (k1BuildingType ? Math.round(1200 / k1BuildingType.percent) : 0)
        : isK1LandImprovements
          ? (k1LandImprovementType ? Math.round(1200 / k1LandImprovementType.percent) : 240)
          : form.years * 12;
  const categoryRow = useMemo(
    () => (categories || []).find((c) => c.code === form.categoryCode) || null,
    [categories, form.categoryCode]
  );
  const isK1Pool = (categoryRow?.accounting_model === 'k1_collective_pool')
    || ['k1_machines_inventory', 'k1_building_inventory', 'k1_vehicles', 'k1_intangibles']
      .includes(form.categoryCode || '');
  // The picker shows exactly the categories the tenant actually has (chart-
  // aware, seeded server-side); without the endpoint, the K2 set is assumed.
  const pickerCategories = useMemo(() => (
    categories
      ? ASSET_CATEGORIES.filter((c) => categories.some((row) => row.code === c.code))
      : ASSET_CATEGORIES.filter((c) => !c.code.startsWith('k1_'))
  ), [categories]);

  // Per-unit assets each carry amount/quantity; the preview shows the total
  // monthly consequence (unit schedule × quantity) so the sentence matches
  // what actually hits resultaträkningen each month.
  const unitAmount = form.grouping === 'per_unit' && quantity > 1
    ? round2(amountNumber / quantity)
    : amountNumber;

  const schedule = useMemo(() => {
    if (!amountNumber || !form.inServiceDate) return null;
    const unitResidual = form.grouping === 'per_unit' && quantity > 1
      ? round2((parseFloat(form.residual) || 0) / quantity)
      : parseFloat(form.residual) || 0;
    const draft = buildDraftSchedule({
      amount: unitAmount,
      residualValue: unitResidual,
      usefulMonths,
      startDate: form.inServiceDate,
      openingAccDepreciation: form.hasOpening ? parseFloat(form.openingAcc) || 0 : 0,
      openingPeriod: form.hasOpening ? form.openingPeriod : null,
    });
    if (!draft.rows.length) return draft;
    const multiplier = form.grouping === 'per_unit' ? quantity : 1;
    return {
      ...draft,
      monthlyAmount: round2(draft.monthlyAmount * multiplier),
      rows: draft.rows.map((row) => ({ ...row, amount: round2(row.amount * multiplier) })),
    };
  }, [amountNumber, unitAmount, usefulMonths, form.inServiceDate, form.hasOpening, form.openingAcc, form.openingPeriod, form.grouping, form.residual, quantity]);

  const openingValid = !form.hasOpening
    || ((parseFloat(form.openingAcc) || 0) > 0 && /^\d{4}-\d{2}$/.test(form.openingPeriod)
      && (parseFloat(form.openingAcc) || 0) < (form.grouping === 'per_unit' && quantity > 1 ? unitAmount : amountNumber));

  const residualNumber = parseFloat(form.residual) || 0;
  const residualValid = residualNumber >= 0 && residualNumber < amountNumber;

  const stepOneValid = Boolean(form.categoryCode) && form.name.trim().length > 0
    && amountNumber > 0 && quantity >= 1 && form.inServiceDate && openingValid
    && (form.residual === '' || residualValid)
    && (!isBuildings || Boolean(buildingType))
    && (!isK1Buildings || Boolean(k1BuildingType))
    && (!isK1LandImprovements || Boolean(k1LandImprovementType));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const toIsoDate = (d) => dateToPeriod(d) + `-${String(d.getDate()).padStart(2, '0')}`;
      const acquisitionDate = form.acquisitionDate || form.inServiceDate;
      const payload = {
        name: form.name.trim(),
        category_code: form.categoryCode,
        ...(categoryRow?.category_id !== undefined && { category_id: categoryRow.category_id }),
        acquisition_value: round2(amountNumber),
        acquisition_date: toIsoDate(acquisitionDate),
        in_service_date: toIsoDate(form.inServiceDate),
        useful_months: usefulMonths,
        // Restvärde får inte användas för byggnader (BFNAR 2017:3 p. 9.25
        // tredje stycket / K2 10.25) och är meningslöst för mark.
        residual_value: (isBuildings || isLand) ? 0 : round2(residualNumber),
        quantity,
        grouping: quantity > 1 ? form.grouping : 'per_unit',
        ...(form.serialNumber.trim() && { serial_number: form.serialNumber.trim() }),
        ...(form.location.trim() && { location: form.location.trim() }),
        // Fordon: the VAT fact is ALWAYS explicit (re-review RR3) — the
        // category default must never be inherited silently. Lastbil/
        // arbetsfordon = deductible VAT → standard (sale gets moms);
        // personbil = no deduction → VAT-exempt sale (Skatteverket,
        // "Bilar och moms"). The backend refuses K1 vehicles without it.
        ...(['vehicles', 'k1_vehicles'].includes(form.categoryCode)
          && { vat_profile: form.vehicleType === 'lastbil' ? 'standard' : 'personbil' }),
        ...(isK1Buildings && k1BuildingType && { k1_building_type: k1BuildingType.value }),
        ...(isK1LandImprovements && k1LandImprovementType && { k1_land_improvement_type: k1LandImprovementType.value }),
        // Spec §5.1 (re-review RR4): accepted deviating tax plan → the
        // engine posts nothing for this asset (standing review line);
        // improvement rows declare the improved building for coherent
        // disposal.
        ...((isK1Buildings || isK1LandImprovements) && form.k1PropertyManualPlan
          && { k1_property_manual_plan: true }),
        ...(isK1Buildings && form.k1ParentAssetId
          && { k1_parent_asset_id: form.k1ParentAssetId }),
        ...(form.hasOpening && {
          opening_acc_depreciation: round2(parseFloat(form.openingAcc) || 0),
          opening_period: form.openingPeriod,
        }),
        ...(prefill?.suggestion_id && { suggestion_id: prefill.suggestion_id }),
        ...(prefill?.supplier_invoice_id && { supplier_invoice_id: prefill.supplier_invoice_id }),
        ...(prefill?.expense_id && { expense_id: prefill.expense_id }),
      };
      const response = await axios.post(getApiUrl(`${companyId}/assets`), payload, { withCredentials: true });
      toast.success('Tillgången är upplagd — LedgerLoop sköter avskrivningen automatiskt.');
      if (onCreated) onCreated(unwrapData(response));
      onHide();
    } catch (error) {
      const message = error.response?.data?.error || 'Kunde inte skapa tillgången. Försök igen.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderConfigure = () => (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Vilken typ av tillgång?</Form.Label>
        <div className="asset-category-picker">
          {pickerCategories.map((category) => {
            const selected = form.categoryCode === category.code;
            const Icon = getCategoryIcon(category.code);
            return (
              <button
                key={category.code}
                type="button"
                className={`asset-category-option${selected ? ' selected' : ''}`}
                aria-pressed={selected}
                onClick={() => setForm((prev) => ({
                  ...prev,
                  categoryCode: category.code,
                  // Category-appropriate life defaults; buildings pick a
                  // byggnadstyp instead of years, land has no plan at all.
                  years: category.code === 'land_improvements' ? 20 : (prev.years > 10 ? 5 : prev.years),
                  buildingType: category.code === 'buildings' ? prev.buildingType : null,
                  residual: ['buildings', 'land'].includes(category.code) ? '' : prev.residual,
                }))}
              >
                <span className="asset-category-icon">
                  <Icon size={18} strokeWidth={1.75} />
                </span>
                <span className="asset-category-label">{category.label}</span>
                {selected && <Check size={14} className="asset-category-check" />}
              </button>
            );
          })}
        </div>
      </Form.Group>

      <Form.Group className="mb-3" controlId="asset-name">
        <Form.Label>Vad?</Form.Label>
        <Form.Control
          type="text"
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder="T.ex. MacBook Pro 14 eller Toyota Hiace"
        />
      </Form.Group>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="asset-amount">
            <Form.Label>Kostade (exkl. moms)</Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => setField('amount', e.target.value)}
                placeholder="0"
              />
              <InputGroup.Text>kr</InputGroup.Text>
            </InputGroup>
            {quantity > 1 && (
              <Form.Text muted>Totalbelopp för alla {quantity} enheter.</Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          {isLand ? (
            <Form.Group className="mb-3" controlId="asset-years">
              <Form.Label>Avskrivning</Form.Label>
              <div className="form-control" style={{ minHeight: 42, display: 'flex', alignItems: 'center', background: '#f8fafc', color: 'var(--secondary)' }}>
                Mark skrivs inte av
              </div>
              <Form.Text muted>
                Mark har obegränsad nyttjandeperiod och skrivs inte av. Värdet ligger kvar tills marken säljs.
              </Form.Text>
            </Form.Group>
          ) : isK1Buildings ? (
            <Form.Group className="mb-3" controlId="asset-k1-building-type">
              <Form.Label>Vilken typ av byggnad?</Form.Label>
              <Select
                options={K1_BUILDING_TYPES.map((b) => ({ value: b.value, label: `${b.label} — ${b.percent} % per år` }))}
                value={k1BuildingType ? { value: k1BuildingType.value, label: `${k1BuildingType.label} — ${k1BuildingType.percent} % per år` } : null}
                onChange={(option) => setField('k1BuildingType', option ? option.value : null)}
                placeholder="Välj byggnadstyp…"
                theme={brandSelectTheme}
                isSearchable={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={{
                  container: (base) => ({ ...base, width: '100%' }),
                  control: (base) => ({ ...base, minHeight: 42, borderWidth: 2, borderColor: '#e2e8f0', borderRadius: 8 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              <Form.Text muted>
                Avskrivningen är skattemässig och följer Skatteverkets procentsats för
                byggnadstypen (SKV A 2005:5; BFNAR 2006:1 p. 6.14).
              </Form.Text>
              {activeBuildings.length > 0 && (
                <div className="mt-2">
                  <Form.Label>Till-/ombyggnad av befintlig byggnad?</Form.Label>
                  <Select
                    options={[{ value: '', label: 'Nej — fristående byggnad' },
                      ...activeBuildings.map((a) => ({ value: a.asset_id, label: `#${a.asset_number} ${a.name}` }))]}
                    value={form.k1ParentAssetId
                      ? { value: form.k1ParentAssetId,
                          label: (activeBuildings.find((a) => a.asset_id === form.k1ParentAssetId) || {}).name || 'Vald byggnad' }
                      : { value: '', label: 'Nej — fristående byggnad' }}
                    onChange={(option) => setField('k1ParentAssetId', option?.value || '')}
                    theme={brandSelectTheme}
                    isSearchable={false}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    styles={{
                      container: (base) => ({ ...base, width: '100%' }),
                      control: (base) => ({ ...base, minHeight: 42, borderWidth: 2, borderColor: '#e2e8f0', borderRadius: 8 }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  <Form.Text muted>
                    En länkad förbättring måste avvecklas före (eller tillsammans med) huvudbyggnaden.
                  </Form.Text>
                </div>
              )}
              <Form.Check
                type="checkbox"
                id="asset-k1-manual-plan"
                className="mt-2"
                label="Godkänd avskrivningsplan avviker från normaltabellen — bokförs manuellt"
                checked={form.k1PropertyManualPlan}
                onChange={(e) => setField('k1PropertyManualPlan', e.target.checked)}
              />
              {form.k1PropertyManualPlan && (
                <Form.Text muted>
                  Motorn bokför ingen avskrivning för tillgången; en stående påminnelse visas vid
                  årsvärderingen tills avskrivningen bokförts manuellt (spec §5.1).
                </Form.Text>
              )}
            </Form.Group>
          ) : isK1LandImprovements ? (
            <Form.Group className="mb-3" controlId="asset-k1-land-improvement-type">
              <Form.Label>Vilken typ av markanläggning?</Form.Label>
              <Select
                options={K1_LAND_IMPROVEMENT_TYPES.map((b) => ({ value: b.value, label: `${b.label} — ${b.percent} % per år` }))}
                value={k1LandImprovementType ? { value: k1LandImprovementType.value, label: `${k1LandImprovementType.label} — ${k1LandImprovementType.percent} % per år` } : null}
                onChange={(option) => setField('k1LandImprovementType', option ? option.value : null)}
                placeholder="Välj typ…"
                theme={brandSelectTheme}
                isSearchable={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={{
                  container: (base) => ({ ...base, width: '100%' }),
                  control: (base) => ({ ...base, minHeight: 42, borderWidth: 2, borderColor: '#e2e8f0', borderRadius: 8 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              <Form.Text muted>
                Avskrivningen är skattemässig: 10 % för täckdiken och skogsvägar,
                5 % för andra markanläggningar (IL 20 kap. 5 §; BFNAR 2006:1 p. 6.15).
              </Form.Text>
              <Form.Check
                type="checkbox"
                id="asset-k1-manual-plan-land"
                className="mt-2"
                label="Godkänd avskrivningsplan avviker från normaltabellen — bokförs manuellt"
                checked={form.k1PropertyManualPlan}
                onChange={(e) => setField('k1PropertyManualPlan', e.target.checked)}
              />
            </Form.Group>
          ) : isK1Pool ? (
            <Form.Group className="mb-3" controlId="asset-years">
              <Form.Label>Uppskattad användningstid</Form.Label>
              <Select
                options={YEAR_OPTIONS}
                value={YEAR_OPTIONS.find((o) => o.value === form.years) || null}
                onChange={(option) => setField('years', option ? option.value : 5)}
                theme={brandSelectTheme}
                isSearchable={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={{
                  container: (base) => ({ ...base, width: '100%' }),
                  control: (base) => ({ ...base, minHeight: 42, borderWidth: 2, borderColor: '#e2e8f0', borderRadius: 8 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              <Form.Text muted>
                Anteckning i förteckningen (BFNAR 2006:1 p. 6.1). Själva avskrivningen
                görs kollektivt vid bokslutet — inte per tillgång.
              </Form.Text>
            </Form.Group>
          ) : isBuildings ? (
            <Form.Group className="mb-3" controlId="asset-building-type">
              <Form.Label>Vilken typ av byggnad?</Form.Label>
              <Select
                options={BUILDING_TYPES.map((b) => ({ value: b.value, label: `${b.label} — ${b.percent} % per år` }))}
                value={buildingType ? { value: buildingType.value, label: `${buildingType.label} — ${buildingType.percent} % per år` } : null}
                onChange={(option) => setField('buildingType', option ? option.value : null)}
                placeholder="Välj byggnadstyp…"
                theme={brandSelectTheme}
                isSearchable={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={{
                  container: (base) => ({ ...base, width: '100%' }),
                  control: (base) => ({ ...base, minHeight: 42, borderWidth: 2, borderColor: '#e2e8f0', borderRadius: 8 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              <Form.Text muted>
                Avskrivningen följer Skatteverkets procentsats för byggnadstypen
                {buildingType ? ` (${buildingType.percent} % = ${Math.round(100 / buildingType.percent)} år)` : ''}.
              </Form.Text>
            </Form.Group>
          ) : (
            <Form.Group className="mb-3" controlId="asset-years">
              <Form.Label>Används i ungefär</Form.Label>
              <Select
                options={isLandImprovements ? LAND_IMPROVEMENT_YEAR_OPTIONS : YEAR_OPTIONS}
                value={(isLandImprovements ? LAND_IMPROVEMENT_YEAR_OPTIONS : YEAR_OPTIONS).find((o) => o.value === form.years) || null}
                onChange={(option) => setField('years', option ? option.value : (isLandImprovements ? 20 : 5))}
                theme={brandSelectTheme}
                isSearchable={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                styles={{
                  container: (base) => ({ ...base, width: '100%' }),
                  control: (base) => ({ ...base, minHeight: 42, borderWidth: 2, borderColor: '#e2e8f0', borderRadius: 8 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              <Form.Text muted>
                {isLandImprovements
                  ? '20 år för de flesta markanläggningar; 10 år för täckdiken och skogsvägar.'
                  : '5 år är standard enligt K2.'}
              </Form.Text>
            </Form.Group>
          )}
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="asset-quantity">
            <Form.Label>Antal</Form.Label>
            <Form.Control
              type="number"
              min="1"
              step="1"
              value={form.quantity}
              onChange={(e) => setField('quantity', e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3" controlId="asset-date">
            <Form.Label>När började den användas?</Form.Label>
            <div>
              <DatePicker
                selected={form.inServiceDate}
                onChange={(date) => setField('inServiceDate', date)}
                dateFormat="yyyy-MM-dd"
                locale={sv}
                className="form-control"
              />
            </div>
            <Form.Text muted>Avskrivningen börjar från den här månaden.</Form.Text>
          </Form.Group>
        </Col>
      </Row>

      {quantity > 1 && (
        <Form.Group className="mb-3">
          <Form.Label>Hur vill du registrera enheterna?</Form.Label>
          <Form.Check
            type="radio"
            id="grouping-per-unit"
            name="grouping"
            label="En post per enhet (rekommenderas)"
            checked={form.grouping === 'per_unit'}
            onChange={() => setField('grouping', 'per_unit')}
          />
          <Form.Check
            type="radio"
            id="grouping-grouped"
            name="grouping"
            label="En gemensam post"
            checked={form.grouping === 'grouped'}
            onChange={() => setField('grouping', 'grouped')}
          />
          {form.grouping === 'grouped' && (
            <Form.Text style={{ color: '#92400e' }}>
              Vid en gemensam post säljs hela gruppen eller ett antal enheter — värdet delas då lika per enhet.
            </Form.Text>
          )}
        </Form.Group>
      )}

      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
        <Form.Check
          type="switch"
          id="asset-has-opening"
          label="Har redan skrivits av tidigare?"
          checked={form.hasOpening}
          onChange={(e) => setField('hasOpening', e.target.checked)}
        />
        {form.hasOpening && (
          <Row className="mt-2">
            <Col md={6}>
              <Form.Group className="mb-2" controlId="asset-opening-acc">
                <Form.Label>Ackumulerade avskrivningar hittills</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.openingAcc}
                    onChange={(e) => setField('openingAcc', e.target.value)}
                    placeholder="0"
                  />
                  <InputGroup.Text>kr</InputGroup.Text>
                </InputGroup>
                {quantity > 1 && form.grouping === 'per_unit' && (
                  <Form.Text muted>Ange beloppet per enhet.</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2" controlId="asset-opening-period">
                <Form.Label>LedgerLoop tar över från och med</Form.Label>
                <Form.Control
                  type="month"
                  value={form.openingPeriod}
                  onChange={(e) => setField('openingPeriod', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Text muted>
                Använd det här när tillgången redan finns i bokföringen sedan tidigare —
                de gamla avskrivningarna bokförs inte om, LedgerLoop fortsätter bara
                schemat därifrån.
              </Form.Text>
            </Col>
          </Row>
        )}
      </div>

      {/* Fordon: the VAT profile decides whether a future sale carries moms —
          must be asked at creation, not discovered at disposal. Applies to
          both the K2 and the K1 vehicle category (review remediation F5). */}
      {['vehicles', 'k1_vehicles'].includes(form.categoryCode) && (
        <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '0.9rem', marginTop: '0.35rem' }}>
          <Form.Label style={{ fontWeight: 600, fontSize: '0.9375rem' }}>Vilken typ av fordon?</Form.Label>
          <Form.Check
            type="radio"
            id="asset-vehicle-personbil"
            name="asset-vehicle-type"
            label="Personbil — momsen fick inte dras av vid köpet, försäljning blir momsfri"
            checked={form.vehicleType === 'personbil'}
            onChange={() => setField('vehicleType', 'personbil')}
            style={{ fontSize: '0.875rem', marginBottom: 4 }}
          />
          <Form.Check
            type="radio"
            id="asset-vehicle-lastbil"
            name="asset-vehicle-type"
            label="Lastbil eller arbetsfordon — momsen drogs av, försäljning bokförs med moms"
            checked={form.vehicleType === 'lastbil'}
            onChange={() => setField('vehicleType', 'lastbil')}
            style={{ fontSize: '0.875rem' }}
          />
        </div>
      )}

      {/* Fler uppgifter — optional register fields (BFNAR 2013:2 p. 4.5):
          restvärde, separat anskaffningsdatum, serienummer, plats. */}
      <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '0.75rem', marginTop: '0.35rem' }}>
        <Button
          variant="link"
          size="sm"
          style={{ textDecoration: 'none', color: 'var(--primary)', paddingLeft: 0 }}
          className="d-inline-flex align-items-center gap-1"
          onClick={() => setShowMore((prev) => !prev)}
        >
          Fler uppgifter {showMore ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </Button>
        <Collapse in={showMore}>
          <div>
            <Row className="mt-2">
              {/* Restvärde får inte användas för byggnader (BFNAR 2017:3
                  p. 9.25 tredje stycket) och saknar mening för mark. */}
              {!isBuildings && !isLand && (
              <Col md={6}>
                <Form.Group className="mb-2" controlId="asset-residual">
                  <Form.Label>Beräknat restvärde</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.residual}
                      onChange={(e) => setField('residual', e.target.value)}
                      placeholder="0"
                      isInvalid={form.residual !== '' && !residualValid}
                    />
                    <InputGroup.Text>kr</InputGroup.Text>
                  </InputGroup>
                  <Form.Text muted>
                    Vad tillgången väntas vara värd när ni slutar använda den — bara den
                    delen över restvärdet skrivs av. De flesta lämnar 0 kr.
                  </Form.Text>
                </Form.Group>
              </Col>
              )}
              <Col md={6}>
                <Form.Group className="mb-2" controlId="asset-acquisition-date">
                  <Form.Label>Anskaffningsdatum</Form.Label>
                  <DatePicker
                    selected={form.acquisitionDate || form.inServiceDate}
                    onChange={(date) => setField('acquisitionDate', date)}
                    dateFormat="yyyy-MM-dd"
                    locale={sv}
                    className="form-control"
                    maxDate={form.inServiceDate}
                  />
                  <Form.Text muted>
                    Om köpet skedde tidigare än den togs i bruk. Annars samma datum.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2" controlId="asset-serial">
                  <Form.Label>Serienummer</Form.Label>
                  <Form.Control
                    value={form.serialNumber}
                    onChange={(e) => setField('serialNumber', e.target.value)}
                    maxLength={100}
                    placeholder="T.ex. C02XL0GWJGH5"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-2" controlId="asset-location">
                  <Form.Label>Plats</Form.Label>
                  <Form.Control
                    value={form.location}
                    onChange={(e) => setField('location', e.target.value)}
                    maxLength={100}
                    placeholder="T.ex. Kontoret, rum 3"
                  />
                  <Form.Text muted>Bra att ha vid inventering och för försäkringen.</Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Collapse>
      </div>
    </>
  );

  const isK1Category = isK1Pool || isK1Buildings || isK1LandImprovements
    || form.categoryCode === 'k1_land';

  const renderReview = () => {
    if (!isLand && !isK1Category && (!schedule || !schedule.rows.length)) {
      return <p className="text-muted mb-0">Fyll i beloppet för att se avskrivningsplanen.</p>;
    }
    if ((isLand || isK1Category) && !amountNumber) {
      return <p className="text-muted mb-0">Fyll i beloppet för att granska.</p>;
    }
    return (
      <>
        <div
          style={{
            background: 'var(--rgba-primary-1)',
            border: '1px solid var(--rgba-primary-2)',
            borderRadius: 12,
            padding: '1rem 1.25rem',
            marginBottom: '1rem',
            fontSize: '0.9375rem',
            color: 'var(--title)',
            lineHeight: 1.55,
          }}
        >
          {isLand ? (
            <>
              Marken registreras till <strong>{formatCurrency(amountNumber)}</strong> och
              skrivs inte av — värdet ligger kvar i balansräkningen tills den säljs.
            </>
          ) : isK1Pool ? (
            <>
              Tillgången registreras till <strong>{formatCurrency(amountNumber)}</strong> och
              värderas <strong>kollektivt</strong> tillsammans med företagets övriga maskiner
              och inventarier vid bokslutet (förenklat årsbokslut). Ingen månadsavskrivning
              bokförs per tillgång.
            </>
          ) : isK1Buildings || isK1LandImprovements ? (
            <>
              Tillgången registreras till <strong>{formatCurrency(amountNumber)}</strong> och
              skrivs av <strong>skattemässigt</strong> med{' '}
              {isK1Buildings ? k1BuildingType?.percent : k1LandImprovementType?.percent} % per år
              i den årliga bokslutsbokningen.
            </>
          ) : (
            <>
              <strong>{formatNumber(schedule.monthlyAmount)} kr</strong> bokförs som kostnad varje
              månad t.o.m. <strong>{formatMonthYear(schedule.endDate, 'long')}</strong>.
              Första verifikationen skapas {formatDate(schedule.firstPostingDate)}.
            </>
          )}
        </div>

        {/* Summary card — what is being created, at a glance. */}
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            padding: '0.9rem 1.1rem',
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <CategoryChip code={form.categoryCode} />
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontWeight: 600, color: 'var(--title)', fontSize: '0.9375rem' }}>
              {form.name}
              {quantity > 1 && (
                <span style={{ fontWeight: 500, fontSize: '0.8125rem', color: 'var(--secondary)', marginLeft: 6 }}>
                  × {quantity} ({form.grouping === 'per_unit' ? 'en post per enhet' : 'en gemensam post'})
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)' }}>
              {ASSET_CATEGORIES.find((c) => c.code === form.categoryCode)?.label}
              {form.categoryCode === 'vehicles' && ` (${form.vehicleType === 'lastbil' ? 'lastbil/arbetsfordon' : 'personbil'})`}
              {isBuildings && buildingType && ` (${buildingType.label.toLowerCase()})`}
              {' · '}{formatCurrency(amountNumber)} exkl. moms
              {' · '}{isLand ? 'skrivs inte av' : isBuildings && buildingType
                ? `${buildingType.percent} % per år (${Math.round(100 / buildingType.percent)} år)`
                : `${form.years} år`}
              {!isBuildings && !isLand && residualNumber > 0 && ` · restvärde ${formatCurrency(residualNumber)}`}
            </div>
          </div>
        </div>

        {!isLand && !isK1Category && (
        <Button
          variant="link"
          size="sm"
          style={{ textDecoration: 'none', color: 'var(--primary)', paddingLeft: 0 }}
          onClick={() => setShowSchedule((prev) => !prev)}
        >
          {showSchedule ? 'Dölj schema' : 'Visa schema'}{' '}
          {showSchedule ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </Button>
        )}
        <Collapse in={showSchedule}>
          <div>
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
              <Table size="sm" className="mb-0 asset-table">
                <thead style={{ position: 'sticky', top: 0 }}>
                  <tr>
                    <th>Period</th>
                    <th>Bokförs</th>
                    <th className="text-end">Belopp</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.rows.map((row) => (
                    <tr key={row.period}>
                      <td>{formatMonthYear(row.periodDate)}</td>
                      <td>{formatDate(row.postingDate)}</td>
                      <td className="text-end asset-num">{formatNumber(row.amount)} kr</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Collapse>

        <div className="mt-2">
          <Button
            variant="link"
            size="sm"
            style={{ textDecoration: 'none', color: 'var(--primary)', paddingLeft: 0 }}
            onClick={() => setShowDetails((prev) => !prev)}
          >
            {showDetails ? 'Dölj detaljer' : 'Visa detaljer'}{' '}
            {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
          <Collapse in={showDetails}>
            <div>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.75rem 1rem', fontSize: '0.8125rem' }}>
                {categoryRow ? (
                  <Row>
                    <Col sm={4}>
                      <div className="text-muted">Tillgångskonto</div>
                      <div style={{ fontWeight: 600 }}>{categoryRow.asset_account}</div>
                    </Col>
                    <Col sm={4}>
                      <div className="text-muted">Ack. avskrivningar</div>
                      <div style={{ fontWeight: 600 }}>{categoryRow.accdep_account}</div>
                    </Col>
                    <Col sm={4}>
                      <div className="text-muted">Avskrivningskostnad</div>
                      <div style={{ fontWeight: 600 }}>{categoryRow.depexp_account}</div>
                    </Col>
                  </Row>
                ) : (
                  <div className="text-muted">
                    Konton väljs automatiskt utifrån kategorin och företagets kontoplan.
                  </div>
                )}
                <Row className="mt-2">
                  <Col sm={4}>
                    <div className="text-muted">Restvärde</div>
                    <div style={{ fontWeight: 600 }}>0 kr</div>
                  </Col>
                  <Col sm={4}>
                    <div className="text-muted">Nyttjandeperiod</div>
                    <div style={{ fontWeight: 600 }}>{form.years} år ({usefulMonths} mån)</div>
                  </Col>
                  <Col sm={4}>
                    <div className="text-muted">Metod</div>
                    <div style={{ fontWeight: 600 }}>Linjär, månadsvis</div>
                  </Col>
                </Row>
              </div>
            </div>
          </Collapse>
        </div>
      </>
    );
  };

  return (
    <Modal show={show} onHide={submitting ? undefined : onHide} size="lg" centered contentClassName="asset-form">
      <Modal.Header closeButton={!submitting} style={{ alignItems: 'flex-start' }}>
        <div>
          <Modal.Title as="h5" style={{ marginBottom: 6 }}>Lägg till tillgång</Modal.Title>
          {/* Step indicator: 1 Uppgifter → 2 Granska */}
          <div className="asset-steps" aria-label={`Steg ${step} av 2`}>
            <span className={`asset-step${step === 1 ? ' active' : ' done'}`}>
              <span className="asset-step-dot">{step > 1 ? <Check size={11} /> : '1'}</span>
              Uppgifter
            </span>
            <span className="asset-step-sep" />
            <span className={`asset-step${step === 2 ? ' active' : ''}`}>
              <span className="asset-step-dot">2</span>
              Granska
            </span>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>{step === 1 ? renderConfigure() : renderReview()}</Modal.Body>
      <Modal.Footer>
        {step === 1 ? (
          <>
            <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
              Avbryt
            </Button>
            <Button variant="primary" disabled={!stepOneValid} onClick={() => setStep(2)}>
              Nästa — granska planen
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline-secondary"
              onClick={() => setStep(1)}
              disabled={submitting}
              className="d-flex align-items-center gap-1"
            >
              <ArrowLeft size={14} /> Tillbaka
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={submitting || !schedule?.rows?.length}>
              {submitting ? 'Skapar…' : 'Klart — sköt det automatiskt'}
            </Button>
          </>
        )}
      </Modal.Footer>

      <style>{ASSET_FORM_STYLES}</style>
      <style>{`
        /* Step indicator in the modal header. */
        .asset-steps { display: flex; align-items: center; gap: 10px; }
        .asset-step { display: inline-flex; align-items: center; gap: 6px; font-size: 0.75rem; font-weight: 600; color: #9ca3af; }
        .asset-step.active { color: var(--primary); }
        .asset-step.done { color: #6b7280; }
        .asset-step-dot { width: 18px; height: 18px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.6875rem; background: #f3f4f6; color: inherit; }
        .asset-step.active .asset-step-dot { background: var(--primary); color: #fff; }
        .asset-step.done .asset-step-dot { background: var(--rgba-primary-1); color: var(--primary); }
        .asset-step-sep { width: 22px; height: 1px; background: #e5e7eb; }

        /* Category picker — icon-chip cards. */
        .asset-category-picker { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 8px; }
        .asset-category-option {
          position: relative;
          display: flex;
          align-items: center;
          gap: 10px;
          border: 2px solid #e2e8f0;
          background: #fff;
          border-radius: 10px;
          padding: 0.6rem 0.75rem;
          font-size: 0.8125rem;
          font-weight: 500;
          color: var(--title);
          cursor: pointer;
          text-align: left;
          transition: border-color 0.15s ease, background 0.15s ease;
        }
        .asset-category-option:hover { border-color: #cbd5e1; }
        .asset-category-option:focus-visible { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--rgba-primary-1); }
        .asset-category-option.selected { border-color: var(--primary); background: var(--rgba-primary-1); font-weight: 600; }
        .asset-category-icon { width: 32px; height: 32px; border-radius: 9px; background: #f3f4f6; color: #6b7280; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; transition: background 0.15s ease, color 0.15s ease; }
        .asset-category-option.selected .asset-category-icon { background: var(--primary); color: #fff; }
        .asset-category-label { line-height: 1.25; }
        .asset-category-check { position: absolute; top: 6px; right: 6px; color: var(--primary); }
      `}</style>
    </Modal>
  );
};

export default CreateAssetForm;
